import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Zap, Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Aktiver() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setError("Manglende token"); return; }
    axios.get(`${API}/public/invitations/${token}`)
      .then(({ data }) => setInv(data))
      .catch((e) => setError(e?.response?.data?.detail || "Ugyldig lenke"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { toast.error("Passordene er ikke like"); return; }
    if (pwd.length < 10) { toast.error("Passordet må være minst 10 tegn"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/invitations/${token}/activate`, { password: pwd });
      setDone(true);
      toast.success("Konto aktivert");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke aktivere");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full bg-[#050505] grid-bg relative overflow-hidden" data-testid="aktiver-page">
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-emerald-500/[0.03] to-transparent" />
      <div className="relative min-h-screen grid place-items-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
              <Zap className="h-5 w-5 text-black" strokeWidth={2.5} />
            </div>
            <div className="font-heading text-xl font-semibold tracking-tight">Krafthub</div>
          </div>

          {error ? (
            <div className="bg-[#0a0a0a] border border-red-500/30 rounded-lg p-6 text-center">
              <XCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
              <div className="font-heading text-lg mb-1">Kan ikke aktivere</div>
              <div className="text-sm text-zinc-400">{error}</div>
            </div>
          ) : done ? (
            <div className="bg-[#0a0a0a] border border-emerald-500/30 rounded-lg p-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
              <div className="font-heading text-lg mb-1">Konto aktivert</div>
              <div className="text-sm text-zinc-400">Du blir sendt til innloggingen…</div>
            </div>
          ) : !inv ? (
            <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Verifiserer lenke…</div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-50">Aktiver bedriftskonto</h1>
                <p className="text-sm text-zinc-400 mt-1">
                  <span className="text-zinc-200">{inv.full_name}</span> · {inv.email}<br />
                  Kunde: <span className="text-emerald-400">{inv.customer?.full_name}</span> hos {inv.tenant?.name}
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">Velg passord</label>
                  <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} minLength={10} required
                    className="w-full h-11 px-3.5 rounded-md bg-zinc-900/70 border border-zinc-800 text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
                    data-testid="aktiver-pwd" />
                  <div className="text-[11px] text-zinc-500 mt-1">Minst 10 tegn</div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">Bekreft passord</label>
                  <input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required
                    className="w-full h-11 px-3.5 rounded-md bg-zinc-900/70 border border-zinc-800 text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
                    data-testid="aktiver-pwd2" />
                </div>
                <button type="submit" disabled={busy}
                  className="w-full h-11 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-medium flex items-center justify-center gap-2"
                  data-testid="aktiver-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Aktiver konto
                </button>
              </form>
              <div className="mt-8 pt-6 border-t border-zinc-900 text-xs text-zinc-500">
                <ShieldCheck className="h-3 w-3 inline text-emerald-400" /> Passordet lagres kryptert (bcrypt). Kontoen får rollen <span className="text-emerald-400">bedriftsbruker</span> med tilgang til bedriftsportalen.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
